'use client';

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

const MenuContext = createContext<{ close: () => void } | null>(null);

interface MenuProps {
  /** Render the trigger; call `toggle` from its onClick. */
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
  children: ReactNode;
}

/**
 * Action dropdown. The panel is portaled to <body> so it never clips inside
 * scroll/overflow containers (tables); it closes on outside click, Escape,
 * scroll and resize.
 */
export function Menu({ trigger, align = 'end', className, children }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openUp = rect.bottom > window.innerHeight - 240;
    setStyle({
      position: 'fixed',
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      ...(align === 'end'
        ? { right: Math.max(8, window.innerWidth - rect.right) }
        : { left: rect.left }),
    });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onMove = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  return (
    <span ref={rootRef} className={cn('relative inline-flex', className)}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && style
        ? createPortal(
            <MenuContext.Provider value={{ close: () => setOpen(false) }}>
              <div
                ref={panelRef}
                role="menu"
                style={style}
                className="corner-brand-sm z-50 min-w-[11rem] border border-zinc-700 bg-zinc-900 py-1 shadow-xl shadow-black/40"
              >
                {children}
              </div>
            </MenuContext.Provider>,
            document.body
          )
        : null}
    </span>
  );
}

interface MenuItemProps {
  onSelect: () => void;
  icon?: LucideIcon;
  danger?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export function MenuItem({ onSelect, icon: Icon, danger, disabled, children }: MenuItemProps) {
  const ctx = useContext(MenuContext);
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        ctx?.close();
        onSelect();
      }}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 pointer-coarse:min-h-11',
        danger ? 'text-danger hover:bg-danger/10' : 'text-zinc-200 hover:bg-white/5'
      )}
    >
      {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden /> : null}
      {children}
    </button>
  );
}
